export const FULL_COMPANY_MANAGEMENT_ROLES = [
  'superadmin',
  'admin',
  'manager',
  'supervisor',
  'employer',
  'hr',
  'administrator',
]

/** Roles that can be limited to specific branches via managedBranchIds. */
export const BRANCH_SCOPED_MANAGEMENT_ROLES = ['manager', 'supervisor']

/** Roles that assign managers to branches (company-wide HR). */
export const COMPANY_WIDE_ASSIGNER_ROLES = [
  'superadmin',
  'admin',
  'employer',
  'hr',
  'administrator',
]

export const getActiveSubCompanyId = (userProfile = {}) => {
  const value = userProfile?.subCompanyId
  if (typeof value !== 'string') return null
  const normalized = value.trim()
  return normalized.length > 0 ? normalized : null
}

export const getActiveBranchId = (userProfile = {}) => {
  const value = userProfile?.branchId
  if (typeof value !== 'string') return null
  const normalized = value.trim()
  return normalized.length > 0 ? normalized : null
}

export const normalizeManagedBranchIds = (value) => {
  if (!Array.isArray(value)) return []
  return [...new Set(value.map((id) => String(id || '').trim()).filter(Boolean))]
}

/**
 * Branches a manager/supervisor is allowed to manage.
 * Empty means company-wide for that role (backward compatible).
 * Does not fall back to branchId — that field is the home/attendance branch.
 */
export const getManagedBranchIds = (userProfile = {}) => {
  return normalizeManagedBranchIds(userProfile?.managedBranchIds)
}

export const isBranchAssignableRole = (role) => {
  const normalized = (role || '').toString().toLowerCase()
  return BRANCH_SCOPED_MANAGEMENT_ROLES.includes(normalized)
}

export const isBranchScopedManager = (userProfile = {}) => {
  if (!isBranchAssignableRole(userProfile?.role)) return false
  return getManagedBranchIds(userProfile).length > 0
}

export const canAssignManagedBranches = (userProfile = {}) => {
  const role = (userProfile?.role || '').toString().toLowerCase()
  if (COMPANY_WIDE_ASSIGNER_ROLES.includes(role)) return true
  // Branch managers may only assign within their own managed set
  return isBranchScopedManager(userProfile)
}

export const canManageBranch = (userProfile, branchId) => {
  if (!isBranchScopedManager(userProfile)) return true
  if (!branchId) return false
  return getManagedBranchIds(userProfile).includes(branchId)
}

/**
 * Whether a branch-scoped manager may manage this employee record.
 * Staff must belong to a managed branch. Other managers are visible when
 * their managed branches overlap (or their home branch is managed).
 */
export const canManageEmployeeRecord = (actor = {}, employee = {}) => {
  if (!isBranchScopedManager(actor)) return true
  const managed = new Set(getManagedBranchIds(actor))
  const employeeBranchId = employee?.branchId || null
  if (employeeBranchId && managed.has(employeeBranchId)) return true
  const theirManaged = getManagedBranchIds(employee)
  if (theirManaged.some((id) => managed.has(id))) return true
  return false
}

export const isSubCompanyModeEnabled = (company = {}) => {
  return company?.settings?.subCompanyModeEnabled === true
}

export const isFullCompanyManagementRole = (userProfile = {}) => {
  const role = (userProfile?.role || '').toString().toLowerCase()
  return FULL_COMPANY_MANAGEMENT_ROLES.includes(role)
}

/**
 * Sub-company id to enforce on queries for scoped users
 * (employees / business_owner). Full company managers return null.
 */
export const getScopedSubCompanyId = (userProfile = {}) => {
  if (isFullCompanyManagementRole(userProfile)) return null
  return getActiveSubCompanyId(userProfile)
}

export const filterBySubCompanyScope = (records = [], userProfile = {}) => {
  if (isFullCompanyManagementRole(userProfile)) return records

  const subCompanyId = getActiveSubCompanyId(userProfile)
  if (!subCompanyId) return records
  return records.filter((record) => (record?.subCompanyId || null) === subCompanyId)
}

/**
 * Staff keep records that were assigned to them even when the saved business id
 * does not match their profile. Company managers already see every business.
 */
export const retainRecordsById = (records = [], userProfile = {}, ownIds = []) => {
  const scoped = filterBySubCompanyScope(records, userProfile)
  if (isFullCompanyManagementRole(userProfile)) return scoped

  const ids = new Set(ownIds.filter(Boolean).map((id) => String(id)))
  if (!ids.size) return scoped

  const seen = new Set(scoped.map((record) => record?.id).filter(Boolean))
  const extra = records.filter((record) => ids.has(String(record?.id || '')) && !seen.has(record?.id))
  return scoped.concat(extra)
}

export const retainOwnRosterEntries = (entries = [], userProfile = {}, userIds = []) => {
  const scoped = filterBySubCompanyScope(entries, userProfile)
  if (isFullCompanyManagementRole(userProfile)) return scoped

  const ids = new Set(userIds.filter(Boolean).map((id) => String(id)))
  if (!ids.size) return scoped

  const seen = new Set(scoped.map((entry) => entry?.id).filter(Boolean))
  const own = entries.filter((entry) =>
    ids.has(String(entry?.employeeId || '')) && !seen.has(entry?.id)
  )
  return scoped.concat(own)
}

/**
 * Restrict branches for branch-scoped managers to managedBranchIds,
 * otherwise for non-management users to their assigned branch / business.
 */
export const filterBranchesForUser = (branches = [], userProfile = {}) => {
  if (isBranchScopedManager(userProfile)) {
    const managed = new Set(getManagedBranchIds(userProfile))
    return branches.filter((branch) => managed.has(branch?.id))
  }

  if (isFullCompanyManagementRole(userProfile)) return branches

  const branchId = getActiveBranchId(userProfile)
  if (branchId) {
    const ownBranch = branches.filter((branch) => branch?.id === branchId)
    if (ownBranch.length > 0) return ownBranch
  }

  return filterBySubCompanyScope(branches, userProfile)
}

/**
 * Restrict employee lists for branch-scoped managers to staff on their
 * branches; otherwise for non-management users to the same business / branch.
 */
export const filterEmployeesForUser = (employees = [], userProfile = {}) => {
  let scoped = filterBySubCompanyScope(employees, userProfile)

  if (isBranchScopedManager(userProfile)) {
    return scoped.filter((employee) => canManageEmployeeRecord(userProfile, employee))
  }

  if (isFullCompanyManagementRole(userProfile)) return scoped

  const branchId = getActiveBranchId(userProfile)
  if (!branchId) return scoped
  return scoped.filter((employee) => (employee?.branchId || null) === branchId)
}

export const applySubCompanyMetadata = (payload = {}, userProfile = {}, fallbackSubCompanyId = null) => {
  const scopedSubCompanyId = getActiveSubCompanyId(userProfile) || fallbackSubCompanyId || null
  return {
    ...payload,
    subCompanyId: scopedSubCompanyId,
  }
}
